import type { Job } from '@mairistem/composants';
import type { TemplateRequestGet } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import { isEmpty, isNil } from 'lodash';
import type BaseTypeRegroupement from '../../classes/typeRegroupement/BaseTypeRegroupement';
import type { RARDropdownValues } from '../../components/simulation/Outils/CalculRARModal';
import type DetailCalcul from '../../interfaces/simulation/detailCalcul';
import type DetailMontantSimulation from '../../interfaces/simulation/detailMontantSimulation';
import type ExportExcelValues from '../../interfaces/simulation/exportExcelValues';
import type FiltresArbitrageMasse from '../../interfaces/simulation/filtresArbitrageMasse';
import type Imputation from '../../interfaces/simulation/imputation';
import type Reports from '../../interfaces/simulation/reports';
import type Service from '../../interfaces/simulation/service';
import type Simulation from '../../interfaces/simulation/simulation';

const url = '/api/finances/simulation/';

export const annulationTransfertComptabilite = (identifiant: number) =>
	request.get<any, any>(`${url}${identifiant}/annulationTransfertComptabilite`);

export const annulationVote = (identifiant: number) =>
	request.get<any, any>(`${url}${identifiant}/annulationVote`);

export const calculImputations = (
	identifiantSimulation: number,
	filters: any,
	data: {
		codeFeuille: string;
		identifiantRegleCalcul: number;
		montantForfaitaire: number;
		pourcentageMajoration: number;
		typeArrondi: number;
		typePeriode: number;
	},
) =>
	request.post<unknown, Job>(
		`${url}${identifiantSimulation}/calculImputations`,
		{ ...data, ...filters },
	);

// Union des services habilites sur les lignes que calculImputations
// calculerait avec les memes filtres (meme selection de lignes cote API, lignes
// sans montant comprises, lignes caduques exclues)
export const getServicesCalculImputations = (
	identifiantSimulation: number,
	filters: Record<string, unknown>,
	data: { codeFeuille: string; typePeriode: number },
) => {
	const params = new URLSearchParams();

	Object.entries({ ...filters, ...data }).forEach(([key, value]) => {
		if (!isNil(value)) {
			params.append(key, String(value));
		}
	});

	return request.get<unknown, Service[]>(
		`${url}${identifiantSimulation}/calculImputations/services?${params.toString()}`,
	);
};

export const calculResteRealise = (
	identifiant: number,
	values: RARDropdownValues,
) => {
	const params = new URLSearchParams();

	Object.entries(values).forEach(([key, value]) => {
		params.append(key, value.toString());
	});

	return request.get<unknown, Job>(
		`${url}${identifiant}/calculResteRealise?${params.toString()}`,
	);
};

export const calculVirementSection = (simulation: Simulation) =>
	request.get<unknown, unknown>(
		`${url}${simulation.identifiant}/calculVirementSection?typePeriode=${simulation.typePeriode}`,
	);

export const getControleCoherence = ({
	identifiant,
	typePeriode,
}: Simulation) =>
	request.get<any, any>(
		`${url}${identifiant}/controleCoherencePrevisions?typePeriode=${typePeriode}`,
	);

export const getDetailCalculImputations = (
	identifiantSimulation: number,
	filters: any,
	typesRegroupement: BaseTypeRegroupement[],
	data: {
		identifiantRegleCalcul: number;
		pourcentageMajoration: number;
		montantForfaitaire: number;
		typeArrondi: number;
		codeFeuille: string;
	},
): Promise<DetailCalcul> => {
	const params = new URLSearchParams();
	params.append(
		'identifiantRegleCalcul',
		data.identifiantRegleCalcul?.toString(),
	);
	params.append(
		'pourcentageMajoration',
		data.pourcentageMajoration?.toString(),
	);
	params.append('montantForfaitaire', data.montantForfaitaire?.toString());
	params.append('typeArrondi', data.typeArrondi?.toString());
	params.append('codeFeuille', data.codeFeuille);
	if (!isEmpty(filters)) {
		Object.entries(filters).forEach(([key, value]) => {
			params.append(key, value.toString());
		});
	}

	typesRegroupement.forEach((typeRegroupement) => {
		params.append('typesRegroupement[]', typeRegroupement.code);
	});

	return request.get(
		`${url}${identifiantSimulation}/detailCalculImputations?${params.toString()}`,
	);
};

// Lance le job de calcul du detail des montants de la simulation, dont le
// resultat est ensuite lu page par page via getDetailMontantsArbitrage
export const lancerDetailMontants = (identifiant: number) =>
	request.get<unknown, Job>(`${url}${identifiant}/detailMontantsArbitrage`);

export const getDetailMontants = (
	identifiant: number,
	identifiantJob: number,
	page: number,
	perPage: number,
	filtres: FiltresArbitrageMasse,
) => {
	const params = new URLSearchParams();

	params.append('identifiantJob', identifiantJob.toString());
	params.append('page', page.toString());
	params.append('perPage', perPage.toString());

	// Filtres appliques par l'API a la lecture du resultat du job
	const listes: Array<[string, Array<string | number>]> = [
		['sections[]', filtres.sections],
		['sens[]', filtres.sens],
		['codesChapitre[]', filtres.codesChapitre],
		['codesArticle[]', filtres.codesArticle],
		['codesOperation[]', filtres.codesOperation],
		['codesFonction[]', filtres.codesFonction],
		['codesAnalytique[]', filtres.codesAnalytique],
		['codesVentilation[]', filtres.codesVentilation],
		['identifiantsLogin[]', filtres.identifiantsLogin],
		['identifiantsService[]', filtres.identifiantsService],
		['identifiantsEtiquette[]', filtres.identifiantsEtiquette],
	];
	listes.forEach(([nom, valeurs]) => {
		valeurs.forEach((valeur) => {
			params.append(nom, String(valeur));
		});
	});
	if (!isNil(filtres.montantMin)) {
		params.append('montantMin', filtres.montantMin.toString());
	}
	if (!isNil(filtres.montantMax)) {
		params.append('montantMax', filtres.montantMax.toString());
	}
	params.append('inclureTraitees', String(filtres.inclureTraitees));
	if (filtres.recherche.trim()) {
		params.append('q', filtres.recherche.trim());
	}

	return request.get<
		unknown,
		TemplateRequestGet<DetailMontantSimulation> & {
			// Somme de demande sur tout le perimetre filtre (pas seulement la page)
			meta: { totalDemande?: number };
		}
	>(`${url}${identifiant}/getDetailMontantsArbitrage?${params.toString()}`);
};

// Utilisateurs ayant saisi au moins une ligne dans le resultat du job
export const getUtilisateursDetailMontants = (
	identifiant: number,
	identifiantJob: number,
) =>
	request.get<
		unknown,
		Array<{
			identifiantLogin: number;
			nom: string | null;
			prenom: string | null;
		}>
	>(
		`${url}${identifiant}/getDetailMontantsArbitrageUtilisateurs?identifiantJob=${identifiantJob}`,
	);

export const getExportExcel = (
	identifiant: number,
	values: ExportExcelValues,
) => {
	const params = new URLSearchParams();

	Object.entries(values).forEach(([key, value]) => {
		params.append(key, value.toString());
	});

	return request.get<any, Job>(
		`${url}${identifiant}/exportExcel?${params.toString()}`,
	);
};

export const getMontantSimulationNotSamePropVoteCount = (identifiant: number) =>
	request.get<any, number>(
		`${url}${identifiant}/getMontantSimulationNotSamePropVoteCount`,
	);

export const getReports = (identifiant: number) =>
	request.get<any, Reports>(`${url}${identifiant}/getReports`);

export const getRepriseRestesARealiser = (identifiant: number) =>
	request.get<any, any>(`${url}${identifiant}/getRepriseRestesARealiser`);

export const getVirementsSection = (simulation: Simulation) =>
	request.get<any, any>(
		`${url}${simulation.identifiant}/getVirementsSection?typePeriode=${simulation.typePeriode}`,
	);

export const getSimulation = (identifiant: number, params?: URLSearchParams) =>
	request.get<unknown, Simulation>(
		`${url}${identifiant}${params ? `?${params}` : ''}`,
	);

export const importBiens = (identifiant: number, identifiantJob: number) =>
	request.get<unknown, Imputation>(
		`${url}${identifiant}/importBiens?identifiantJob=${identifiantJob}`,
	);

export const importEmprunt = (identifiant: number, identifiantJob: number) =>
	request.get<unknown, Job>(
		`${url}${identifiant}/importEmprunt?identifiantJob=${identifiantJob}`,
	);

export const propToVote = (identifiant: number, shouldResetMontant: boolean) =>
	request.get<any, any>(
		`${url}${identifiant}/propToVote?shouldResetMontant=${shouldResetMontant}`,
	);

export const transfertComptabilite = (identifiant: number) =>
	request.get<any, any>(`${url}${identifiant}/transfertComptabilite`);

export const updateSimulation = (
	identifiantSimulation: number,
	data: Simulation,
) =>
	request.put<Partial<Simulation>, Simulation>(
		`${url}${identifiantSimulation}`,
		data,
	);

export const recopieReport = (simulation: Simulation, values?: object) => {
	const params = new URLSearchParams();

	if (values) {
		Object.keys(values).forEach((key) => {
			const value = values[key];
			if (!isEmpty(value)) params.append(key, value);
		});
	}

	params.append('typePeriode', String(simulation?.typePeriode));

	return request.get<unknown, unknown>(
		`${url}${simulation?.identifiant}/recopieReport?${params.toString()}`,
	);
};

export const repriseRestesARealiser = (identifiant: number) =>
	request.get<any, Job>(`${url}${identifiant}/repriseRestesARealiser`);

export const sendImportExcel = (
	identifiant: number,
	file: FormData,
	substitute: boolean,
) =>
	request.upload<unknown, Job>(
		`${url}${identifiant}/importExcel?substitute=${substitute}`,
		file,
	);
